// PagGiftPreview.jsx
import React, { useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

import PAGCanvas from '../component/PAGCanvas.jsx';
import { useGiftPreview } from '../context/GiftPreviewContext.jsx';
import { NULL_FUNCTION } from '../resource/defaults.js';
import { isPagCached } from '../resource/getPag.js';

/**
 * PAG animation overlay for a gift card; wires into `GiftPreviewContext` so
 * the card's SendButton shares the same loading / error state as mobile.
 * @param {object} props
 * @param {string} props.id - Gift id; also used as `loadingPreviewGiftId`.
 * @param {string} props.pagImage - PAG asset URL passed to PAGCanvas.
 * @param {function} [props.onSuccess] - Fires when PAG has started playing.
 * @param {function} [props.onFailure] - Fires when PAG fetch / parse fails.
 * @param {function} [props.onUnmount] - Fires once on unmount.
 * @returns {React.ReactElement}
 * @example
 * <PagGiftPreview id="rose" pagImage=".../rose.pag" onSuccess={fn} />
 */
const PagGiftPreview = ({
  id,
  pagImage,
  onSuccess = NULL_FUNCTION,
  onFailure = NULL_FUNCTION,
  onUnmount = NULL_FUNCTION,
}) => {
  const context = useGiftPreview();
  const startPreviewLoading = context?.startPreviewLoading;
  const endPreviewLoading = context?.endPreviewLoading;
  // Ref-stashed so the unmount-only effect below keeps empty deps; a fresh
  // callback ref from parent would otherwise re-fire cleanup → false unmount.
  const onUnmountRef = useRef(onUnmount);
  useEffect(() => {
    onUnmountRef.current = onUnmount;
  }, [onUnmount]);

  // Mount dispatches loading; matching end comes from onPlay / onError or
  // from the cleanup (covers fast unhover before play). Skip when PAG is
  // already cached — `getPlayer` resolves in 1-2 microtasks so the SendButton
  // would otherwise flash a spinner on warm-cache hover.
  useEffect(() => {
    if (!startPreviewLoading || !endPreviewLoading || !id) return;
    if (isPagCached(pagImage)) return;
    startPreviewLoading(id);
    return () => endPreviewLoading(id);
  }, [id, pagImage, startPreviewLoading, endPreviewLoading]);

  // Fires once on unmount so the parent can clear "PAG is playing" + re-show PNG.
  useEffect(() => {
    return () => onUnmountRef.current();
  }, []);

  const handlePlay = useCallback(() => {
    endPreviewLoading?.(id);
    onSuccess();
  }, [endPreviewLoading, id, onSuccess]);
  const handleError = useCallback(() => {
    endPreviewLoading?.(id, { hasError: true });
    onFailure();
  }, [endPreviewLoading, id, onFailure]);

  return (
    <PAGCanvas
      key={`pag-preview-${id}`}
      src={pagImage}
      shouldPlay={true}
      repeat={0}
      onPlay={handlePlay}
      onError={handleError}
    />
  );
};

PagGiftPreview.propTypes = {
  id: PropTypes.string.isRequired,
  pagImage: PropTypes.string.isRequired,
  onSuccess: PropTypes.func,
  onFailure: PropTypes.func,
  onUnmount: PropTypes.func,
};

export default PagGiftPreview;
